import React, { useContext, useState, useRef, useEffect, useCallback } from "react";
import { FaBell, FaCheckDouble } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";

const API = "/api/notificaciones";
const INTERVALO_POLL_MS = 30000;

const formatoFecha = (fecha) =>
  new Date(fecha).toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function NotificacionesBell() {
  const { isAuthenticated } = useContext(AuthContext);
  const [abierto, setAbierto] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const menuRef = useRef();

  const cargar = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch(API, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setNotificaciones(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando notificaciones:", error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    cargar();
    const intervalo = setInterval(cargar, INTERVALO_POLL_MS);
    return () => clearInterval(intervalo);
  }, [cargar]);

  useEffect(() => {
    const handler = (e) => {
      if (!menuRef.current?.contains(e.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const noLeidas = notificaciones.filter((n) => !n.leida).length;

  const marcarLeida = async (id) => {
    setNotificaciones((prev) => prev.map((n) => (n.id === id ? { ...n, leida: true } : n)));
    try {
      await fetch(`${API}/${id}/leer`, { method: "PATCH", credentials: "include" });
    } catch (error) {
      console.error("Error marcando notificación:", error);
    }
  };

  const marcarTodasLeidas = async () => {
    setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })));
    try {
      await fetch(`${API}/leer-todas`, { method: "PATCH", credentials: "include" });
    } catch (error) {
      console.error("Error marcando notificaciones:", error);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setAbierto((v) => !v)}
        className="relative w-9 h-9 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition"
        title="Notificaciones"
      >
        <FaBell className="text-base" />
        {noLeidas > 0 && (
          <span className="absolute top-1 right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-semibold leading-none">
            {noLeidas > 9 ? "9+" : noLeidas}
          </span>
        )}
      </button>

      {abierto && (
        <div className="absolute right-0 mt-2 w-80 bg-white text-slate-700 shadow-lg border border-slate-200 rounded-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-800">Notificaciones</p>
            {noLeidas > 0 && (
              <button
                onClick={marcarTodasLeidas}
                className="flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <FaCheckDouble className="text-[10px]" /> Marcar todas
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notificaciones.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No tienes notificaciones.</p>
            ) : (
              notificaciones.map((n) => (
                <button
                  key={n.id}
                  onClick={() => marcarLeida(n.id)}
                  className={`w-full text-left px-3.5 py-2.5 border-b border-slate-50 last:border-b-0 hover:bg-slate-50 transition ${
                    n.leida ? "" : "bg-indigo-50/60"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.leida && <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 mt-1.5 shrink-0" />}
                    <div className={n.leida ? "pl-3.5" : ""}>
                      <p className="text-xs text-slate-700 leading-snug">{n.mensaje}</p>
                      <p className="text-[11px] text-slate-400 mt-1">{formatoFecha(n.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
